import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { faPlus, faSearch, faEye } from '@fortawesome/free-solid-svg-icons';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { BalanceAccountService } from 'src/app/share/services/balance-account/balance-account.service';
import { BalanceAccountDetailComponent } from './balance-account-detail/balance-account-detail.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-balance-account',
  standalone: false,
  templateUrl: './balance-account.component.html',
  styleUrl: './balance-account.component.scss',
})
export class BalanceAccountComponent implements OnInit, OnDestroy {
  private readonly searchSubject$ = new Subject<string>();
  private searchSubscription!: Subscription;
  protected readonly faPlus = faPlus;
  protected readonly faSearch = faSearch;
  protected readonly faEye = faEye;

  loader: boolean = false;
  accountList: any[] = [];
  errorMessage: string = '';

  filters = {
    searchTerm: '',
  };
  highlights = {
    accounts:0,
    activeAccounts:0,
    balance:0,
  }

  colors: string[] = [
    '#0369A1', 
    '#A16207',
    '#7C3AED', 
    '#9D174D',
    '#047857',
    '#4F46E5',
    '#0D9488',
    '#DC2626',
    '#2563EB',
    '#EA580C'
  ];

  constructor(
    private readonly balanceAccountService: BalanceAccountService,
    private readonly offcanvasService: NgbOffcanvas,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.getAccounts();
    this.getAccountsSummary();

    this.searchSubscription = this.searchSubject$.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe((val: string) => {
      this.filters.searchTerm = val.trim();
      this.onFilterChange();
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) this.searchSubscription.unsubscribe();
  }

  getAccountsSummary(): void {
    this.balanceAccountService.getBalanceAccountsSummary({}).subscribe({
      next: (res: any) => {
        this.highlights.accounts = res?.data?.totalAccounts || 0;
        this.highlights.activeAccounts = res?.data?.totalActiveAccounts || 0;
        this.highlights.balance = res?.data?.totalBalance || 0;
      }
    });
  }

  getAccounts(): void {
    this.loader = true;
    this.errorMessage = '';

    const payload: any = { };

    if (this.filters.searchTerm) {
      payload.searchTerm = this.filters.searchTerm;
    }
    this.balanceAccountService.getBalanceAccounts(payload).subscribe({
      next: (res: any) => {
        const data = res.data || [];
        const formattedData = data.map((acc: any, index: number) => {
          const charCodeSum = acc._id ? acc._id.split('').reduce((sum: number, char: string) => sum + (char.codePointAt(0) ?? 0), 0) : index;
          const colorIndex = charCodeSum % this.colors.length;
          return {
            ...acc,
            badgeColor: this.colors[colorIndex]
          };
        });

        this.accountList = formattedData;
        this.loader = false;
      },
      error: (err: any) => {
        this.loader = false;
        this.errorMessage = err?.error?.error?.message || err?.error?.message || 'Failed to fetch balance accounts';
      }
    });
  }

  onFilterChange(): void {
    this.accountList = [];
    this.getAccounts();
  }

  onSearchInput(val: string): void {
    this.searchSubject$.next(val);
  }

  openAddAccountModal(): void {
    const offcanvasRef = this.offcanvasService.open(BalanceAccountDetailComponent, {
      position: 'end',
      panelClass: 'balance-account-detail-offcanvas',
      backdrop: 'static',
      keyboard: false
    });

    offcanvasRef.componentInstance.mode = 'ADD';
    offcanvasRef.componentInstance.accountData = null;
    offcanvasRef.result.then((result) => {
      if (result === 'SAVED') {
        this.onFilterChange();
      }
    }, () => {});
  }

  openAccountDetail(account: any): void {
    const offcanvasRef = this.offcanvasService.open(BalanceAccountDetailComponent, {
      position: 'end',
      panelClass: 'balance-account-detail-offcanvas',
      backdrop: 'static',
      keyboard: false
    });

    offcanvasRef.componentInstance.mode = 'VIEW';
    offcanvasRef.componentInstance.accountData = account;
    offcanvasRef.result.then((result) => {
      if (result === 'SAVED') {
        this.onFilterChange();
      }
    }, () => {});
  }

  navigateToTransfers(account: any): void {
    this.balanceAccountService.setSelectedAccount(account);
    this.router.navigate(['/balance-transfers', account._id]);
  }
}

